import type { WorkbookPrintRequest } from '@mathflat/domain/RemotePrint'
import type { Workbook } from '@mathflat/domain/Workbook'
import { action, computed, makeObservable, observable } from 'mobx'

import { WorkbookPrintModal } from '@/contentComponents/modal/Print'
import { _workbookController } from '@/controller/_workbook.controller'
import { printController } from '@/controller/print.controller'
import { CommonError, DTO, PrintPageInfo, PrintRangeOption } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { stringUtils } from '@/utils/String'
import { commonErrorMessage, toastError, toastSuccess } from '@/utils/toast'

import globalLoaderService from '../globalLoader.service'
import modalService from '../modal.service'
import { localStorageService } from '../storage.service'
import { CommonPrintService } from './commonPrint.service'

class WorkbookPrintService extends CommonPrintService {
  printPageInfoList: PrintPageInfo[] | null = null
  uiStore = new UIStore()
  rangeOption = new PrintRangeOption('WORKBOOK')

  constructor() {
    super()

    makeObservable<
      WorkbookPrintService,
      'getPage' | 'setRecentPrintOption' | 'fetchWorkbookDetail' | 'fetchPrintWorkbook'
    >(this, {
      printPageInfoList: observable,
      uiStore: observable,
      rangeOption: observable,
      fetchInitPrintList: action,
      onPrint: action,
      onChangePageInput: action,
      onChangeRangeOption: action,
      openWorkbookPrintModal: action,
      disabledPrint: computed,
      isSinglePrintTarget: computed,
      maximumPageRange: computed,

      // private
      getPage: action,
      setRecentPrintOption: action,
      fetchPrintWorkbook: action,
      fetchWorkbookDetail: action,
    })
  }

  get disabledPrint() {
    if (this.uiStore.visibleRangeOption === 'SELECT') {
      return !this.rangeOption.inputText
    }
    return false
  }

  get isSinglePrintTarget() {
    return this.printPageInfoList?.length === 1
  }

  get maximumPageRange() {
    if (!this.printPageInfoList) return '1-1'
    const { initPage, elementPage, quickAnswerPage } = this.printPageInfoList[0]

    const maxPage = Math.max(initPage, elementPage, quickAnswerPage)
    return `1-${maxPage}`
  }

  async fetchInitPrintList(printTargetInfo: number[]) {
    try {
      const recentUiStore = localStorageService.getItem('RECENT_WORKBOOK_PRINT_UI_OPTION')
      if (recentUiStore) {
        this.uiStore.update({ ...recentUiStore })
      }

      const printPageInfoList = await Promise.all(
        printTargetInfo.map((targetId) => this.fetchWorkbookDetail(targetId)),
      )

      this.printPageInfoList = printPageInfoList

      const { elementPage, quickAnswerPage } = this.printPageInfoList[0]
      const inputText = this.getPage({
        elementPage,
        quickAnswerPage,
      })

      this.rangeOption.update({ inputText })
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  async onPrint(printTargetIds: number[]) {
    try {
      if (!this.printAgent) {
        throw new CommonError('프린트가 연결되지 않았습니다.', { type: 'client' })
      }

      globalLoaderService.openLoader()
      await this.fetchPrintWorkbook(printTargetIds)
      this.setRecentPrintOption()
      this.resetPrintRangeCopyOptions()
      modalService.closeModal()
      globalLoaderService.closeLoader()
      toastSuccess('인쇄 요청에 성공했습니다.')
    } catch (error) {
      if (error instanceof CommonError && error.type === 'client') {
        toastError(error.message)
      }
      globalLoaderService.closeLoader()
      this.printErrorNotice(error)
    }
  }

  onChangePageInput(inputText: string) {
    workbookPrintService.rangeOption.update({
      inputText,
      problem: false,
      answer: false,
    })
  }

  onChangeRangeOption(rangeOption: Partial<PrintRangeOption>) {
    if (!this.printPageInfoList) return
    const { elementPage, quickAnswerPage } = this.printPageInfoList[0]
    this.rangeOption.update({
      ...rangeOption,
    })

    const inputText = this.getPage({
      elementPage,
      quickAnswerPage,
    })

    this.rangeOption.update({ inputText })
  }

  async openWorkbookPrintModal(workbookIds: Workbook['id'][], afterPrintFn: () => void) {
    try {
      await this.fetchPrintList()
      if (this.printAgent) {
        modalService.openModal(
          <WorkbookPrintModal printTargetIds={workbookIds} afterPrintFn={afterPrintFn} />,
        )
      }
    } catch (error) {
      this.printErrorNotice(error)
    }
  }

  private getPage = ({
    elementPage,
    quickAnswerPage,
    chapterCoverPages,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    elementPage: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    quickAnswerPage: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    chapterCoverPages?: any
  }) => {
    // 범위 계산
    // 문제 범위 minPage ~ answerPage
    // 정답 범위 answerPage ~ maxPage
    if (this.uiStore.visibleRangeOption === 'ALL') {
      if (this.printOption.excludeCoverPage && chapterCoverPages) {
        return stringUtils.generateNumberScope(this.maximumPageRange, chapterCoverPages)
      }

      return this.maximumPageRange
    }

    // 직접 입력한 경우
    if (this.rangeOption.allNotSelect) {
      if (this.printOption.excludeCoverPage && chapterCoverPages) {
        return stringUtils.generateNumberScope(this.rangeOption.inputText, chapterCoverPages)
      }

      return this.rangeOption.inputText
    }

    const range = {
      problem: `1-${elementPage}`,
      answer: `${elementPage + 1}-${quickAnswerPage}`,
    }

    let text = ''

    for (const option in range) {
      if (this.rangeOption[option]) {
        text += `${range[option]},`
      }
    }

    const result = text.slice(0, text.length - 1) //  마지막 쉼표 제거

    if (this.printOption.excludeCoverPage && chapterCoverPages) {
      return stringUtils.generateNumberScope(result, chapterCoverPages)
    }

    return result
  }

  // 최근 사용한 프린트 옵션 저장하기
  private setRecentPrintOption() {
    this.setPrintOption()
    localStorageService.setItem('RECENT_WORKBOOK_PRINT_UI_OPTION')(this.uiStore)
  }

  // setRecentPrintOption에서 저장하지 않는 매수, 범위 옵션 리셋
  private resetPrintRangeCopyOptions() {
    this.printOption.copyNumber = 1
    this.rangeOption.reset()
  }

  private async fetchPrintWorkbook(workbookIds: number[]) {
    const books = workbookIds.map((workbookId) => {
      return {
        bookCode: workbookId,
        page: (() => {
          let page = ''
          const target = this.printPageInfoList?.find(
            (pageInfo) => pageInfo.targetId === workbookId,
          )

          if (target) {
            page = this.getPage({
              elementPage: target.elementPage,
              quickAnswerPage: target.quickAnswerPage,
              chapterCoverPages: target.coverPages?.chapterCoverPages,
            })
          } else if (this.uiStore.visibleRangeOption === 'ALL') {
            page = this.maximumPageRange
          } else {
            page = this.rangeOption.inputText
          }

          return target ? this.getDividedProblemAndAnswerPage(page, [target?.elementPage]) : page
        })(),
      }
    })

    if (commonRepo.user?.teacherId) {
      const data: WorkbookPrintRequest = {
        ...this.printOption.params,
        books,
        teacherId: commonRepo.user.teacherId,
      }

      await printController.postMQTTPrintCustomWorkbook(data)
    }
  }

  private async fetchWorkbookDetail(targetId: number) {
    const { minPage, answerPage, maxPage, coverPages } =
      await _workbookController.fetchWorkbookDetailInfo(targetId)
    const printWorkbookInfo = new PrintPageInfo({
      initPage: minPage,
      elementPage: answerPage - 1,
      quickAnswerPage: maxPage,
      targetId,
      coverPages,
    })
    return printWorkbookInfo
  }
}

class UIStore extends DTO {
  visibleRangeOption: 'ALL' | 'SELECT' = 'SELECT'

  constructor() {
    super()

    makeObservable(this, {
      visibleRangeOption: observable,
    })
  }
}

export const workbookPrintService = new WorkbookPrintService()
